/**
 * Données de pilotage d'Artosera (base plateforme, ARTO-58).
 *
 * Grille tarifaire, revenus ponctuels, sommes dues, coûts, partage entre
 * associés et jalons : tout ce que la vue d'aigle affiche et que la direction
 * ou l'exploitation saisit.
 *
 * Aucun montant n'a de valeur par défaut autre que `null` : les tarifs
 * changent, et c'est la direction qui les saisit. Un `null` s'affiche « à
 * saisir » et n'entre dans aucun total.
 *
 * Chaque ressource se déclare en trois temps : ses champs SANS valeur par
 * défaut (la forme d'une mise à jour, où un champ absent doit rester absent),
 * les mêmes avec leurs défauts (la forme d'une création), et le document
 * stocké (identifiant, horodatage, auteurs).
 */
import { z } from 'zod';
import { palierSchema } from '../modules.js';
import { TAUX_NORMAL } from '../tax/regimes.js';
import { centimesPositifsSchema, dateSaisieSchema, idSchema, POINTS_DE_BASE, tauxPointsDeBaseSchema, } from './commun.js';
/** Un mois comptable, `AAAA-MM`. */
export const moisSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois attendu : AAAA-MM.');
/** Un montant saisi ; `null` = à saisir. */
const montant = centimesPositifsSchema.nullable();
const texte = (max) => z.string().trim().min(1).max(max);
/**
 * Ce que la base pose sur chaque document de pilotage : identifiant,
 * horodatage et auteurs, à la façon de la base plateforme (`creeParId`,
 * `modifieParId`), et non de `withAudit`.
 */
export const champsSuiviPlateforme = {
    _id: idSchema,
    createdAt: z.date(),
    updatedAt: z.date(),
    /** Compte de la console qui a créé la ligne ; `null` pour un travail de fond. */
    creeParId: idSchema.nullable(),
    modifieParId: idSchema.nullable(),
};
// ── Grille tarifaire — `platform_tariffs` (4.1) ──────────────────────────────
export const TYPES_TARIF = ['palier', 'forfait_exploitant'];
export const typeTarifSchema = z.enum(TYPES_TARIF);
const champsTarif = z.object({
    type: typeTarifSchema,
    libelle: texte(80),
    palier: palierSchema.nullable(),
    galeriesMin: z.int().min(1).nullable(),
    /** `null` = sans plafond. */
    galeriesMax: z.int().min(1).nullable(),
    montantMensuelCentimes: montant,
    ordre: z.int().min(0),
    actif: z.boolean(),
});
const champsTarifCrees = champsTarif.extend({
    palier: champsTarif.shape.palier.default(null),
    galeriesMin: champsTarif.shape.galeriesMin.default(null),
    galeriesMax: champsTarif.shape.galeriesMax.default(null),
    montantMensuelCentimes: montant.default(null),
    ordre: champsTarif.shape.ordre.default(0),
    actif: champsTarif.shape.actif.default(true),
});
/**
 * Un palier nomme son palier ; une tranche de forfait a un minimum, et un
 * plafond qui ne lui est pas inférieur. Vérifié aussi par la route sur le
 * document fusionné d'une mise à jour.
 */
function coherenceTarif(tarif, ctx) {
    if (tarif.type === 'palier' && tarif.palier == null) {
        ctx.addIssue({ code: 'custom', path: ['palier'], message: 'Palier requis.' });
    }
    if (tarif.type === 'forfait_exploitant') {
        if (tarif.galeriesMin == null) {
            ctx.addIssue({ code: 'custom', path: ['galeriesMin'], message: 'Minimum requis.' });
        }
        else if (tarif.galeriesMax != null && tarif.galeriesMax < tarif.galeriesMin) {
            ctx.addIssue({
                code: 'custom',
                path: ['galeriesMax'],
                message: 'Plafond inférieur au minimum.',
            });
        }
    }
}
export const tarifSchema = champsTarifCrees
    .extend(champsSuiviPlateforme)
    .superRefine(coherenceTarif);
export const tarifCreationSchema = champsTarifCrees.superRefine(coherenceTarif);
export const tarifMiseAJourSchema = champsTarif.partial();
// ── Services comptables (comptabilité de la vue d'aigle) ─────────────────────
/**
 * Les services entre lesquels la comptabilité ventile les recettes, dans
 * l'ordre de l'écran. `abonnement` se lit dans les abonnements ; les quatre
 * autres, dans les revenus ponctuels classés (`comptabilite.ts`).
 */
export const SERVICES_COMPTABLES = [
    'abonnement',
    'integration',
    'migration',
    'site_galerie',
    'add_on',
];
export const serviceComptableSchema = z.enum(SERVICES_COMPTABLES);
/**
 * L'identifiant d'un TYPE DE RECETTE : l'un des cinq services de base (sa clé,
 * stable depuis toujours), ou l'identifiant d'un type que la direction a créé
 * (`platform_revenue_types`, 24 caractères hexadécimaux). Jamais un libellé :
 * renommer un type ne touche ni une version de grille, ni une recette, ni un
 * mois clôturé. Les deux formes ne peuvent pas se confondre.
 */
export const typeRecetteIdSchema = z.union([serviceComptableSchema, idSchema]);
/**
 * Le nom français de chaque service de base, quand le SERVEUR doit en écrire
 * un : la catégorie et le libellé par défaut d'un encaissement. L'écran a les
 * siens, dans sa langue (`apps/console`, `comptabilite.services`).
 */
export const LIBELLES_SERVICES_COMPTABLES = {
    abonnement: 'Abonnements',
    integration: 'Intégration',
    migration: 'Migration',
    site_galerie: 'Création de site galerie',
    add_on: 'Add-ons',
};
/** Un service de base, par opposition à un type de recette créé. */
export function estServiceDeBase(id) {
    return SERVICES_COMPTABLES.includes(id);
}
/**
 * Taux de TVA d'une recette quand rien n'est saisi : le taux normal français,
 * 20 %. C'est la loi, pas un tarif — la seule valeur par défaut admise sur une
 * ligne de recette. Modifiable ligne par ligne : 0 pour une autoliquidation ou
 * un export.
 */
export const TAUX_TVA_RECETTE_PAR_DEFAUT = TAUX_NORMAL;
/** Le taux de TVA d'une recette, en points de base. */
export const tauxTvaRecetteSchema = tauxPointsDeBaseSchema;
// ── Revenus ponctuels — `platform_revenues` (4.3) ────────────────────────────
const champsRevenu = z.object({
    mois: moisSchema,
    /**
     * Texte libre : la ventilation évolue sans migration. 80 caractères, comme
     * le nom d'un type de recette, qu'un encaissement y recopie.
     */
    categorie: texte(80),
    libelle: texte(160),
    montantCentimes: montant,
    /**
     * Le service de la comptabilité ; `null` = à classer. Une ligne à classer
     * n'est pas répartie, et un mois qui en porte ne se clôture pas. Les lignes
     * antérieures au champ restent à classer : on ne devine pas leur service
     * d'après `categorie`, qui reste libre.
     */
    service: serviceComptableSchema.nullable(),
    /** Hors taxes : `montantCentimes` est le HT de la ligne, la TVA s'en déduit. */
    tauxTvaPointsDeBase: tauxTvaRecetteSchema,
    /**
     * La galerie que la recette concerne ; `null` = non rattachée, et aucune
     * exclusion de galerie ne s'y applique (comptabilité). Elle existe et n'est
     * pas un espace de test : contrôle de la route.
     */
    galerieId: idSchema.nullable(),
});
const champsRevenuCrees = champsRevenu.extend({
    montantCentimes: montant.default(null),
    service: champsRevenu.shape.service.default(null),
    galerieId: idSchema.nullable().default(null),
    tauxTvaPointsDeBase: tauxTvaRecetteSchema.default(TAUX_TVA_RECETTE_PAR_DEFAUT),
});
export const revenuSchema = champsRevenuCrees.extend(champsSuiviPlateforme);
export const revenuCreationSchema = champsRevenuCrees;
export const revenuMiseAJourSchema = champsRevenu.partial();
// ── Sommes dues — `platform_receivables` (4.4) ───────────────────────────────
export const ETATS_CREANCE = ['due', 'partielle', 'reglee', 'annulee'];
export const etatCreanceSchema = z.enum(ETATS_CREANCE);
const champsCreance = z.object({
    galerieId: idSchema.nullable(),
    groupeId: idSchema.nullable(),
    debiteur: texte(200),
    libelle: texte(160),
    echeanceLe: dateSaisieSchema,
    montantCentimes: montant,
    encaisseCentimes: centimesPositifsSchema,
    etat: etatCreanceSchema,
    regleeLe: dateSaisieSchema.nullable(),
});
const champsCreanceCrees = champsCreance.extend({
    galerieId: idSchema.nullable().default(null),
    groupeId: idSchema.nullable().default(null),
    montantCentimes: montant.default(null),
    encaisseCentimes: centimesPositifsSchema.default(0),
    etat: etatCreanceSchema.default('due'),
    regleeLe: dateSaisieSchema.nullable().default(null),
});
export const creanceSchema = champsCreanceCrees.extend(champsSuiviPlateforme);
export const creanceCreationSchema = champsCreanceCrees;
export const creanceMiseAJourSchema = champsCreance.partial();
// ── Coûts — `platform_costs` (4.5) ───────────────────────────────────────────
const champsCout = z.object({
    mois: moisSchema,
    /** Texte libre ; un seul coût par poste et par mois. */
    poste: texte(80),
    montantCentimes: montant,
});
const champsCoutCrees = champsCout.extend({ montantCentimes: montant.default(null) });
export const coutSchema = champsCoutCrees.extend(champsSuiviPlateforme);
export const coutCreationSchema = champsCoutCrees;
export const coutMiseAJourSchema = champsCout.partial();
// ── Partage entre associés — `platform_partners` (4.6) ───────────────────────
const champsBeneficiaire = z.object({
    libelle: texte(120),
    /** Le compte associé du bénéficiaire, s'il en a un. */
    compteId: idSchema.nullable(),
    /**
     * `null` = à saisir. La somme des parts saisies ne dépasse pas 10 000 :
     * contrôle de la route, qui voit toutes les lignes.
     */
    partPointsDeBase: z.int().min(0).max(POINTS_DE_BASE).nullable(),
    /**
     * Reçoit la part qu'un autre bénéficiaire laisse sur une galerie qu'il
     * exclut (comptabilité). UN bénéficiaire au plus, qui n'exclut aucune
     * galerie : contrôle de la route, sous le verrou du partage.
     */
    recoitLeReste: z.boolean(),
});
const champsBeneficiaireCrees = champsBeneficiaire.extend({
    compteId: idSchema.nullable().default(null),
    partPointsDeBase: champsBeneficiaire.shape.partPointsDeBase.default(null),
    recoitLeReste: z.boolean().default(false),
});
export const beneficiaireSchema = champsBeneficiaireCrees.extend(champsSuiviPlateforme);
export const beneficiaireCreationSchema = champsBeneficiaireCrees;
export const beneficiaireMiseAJourSchema = champsBeneficiaire.partial();
// ── Versements aux associés — `platform_partner_payouts` (4.6) ───────────────
const champsVersement = z.object({
    beneficiaireId: idSchema,
    mois: moisSchema,
    /**
     * Saisi par la direction, ou écrit par la CLÔTURE d'un mois de la
     * comptabilité — un geste de la direction, jamais un calcul en tâche de
     * fond : la console n'écrit pas part × marge d'elle-même.
     */
    montantDuCentimes: montant,
    verseCentimes: centimesPositifsSchema,
    verseLe: dateSaisieSchema.nullable(),
});
const champsVersementCrees = champsVersement.extend({
    montantDuCentimes: montant.default(null),
    verseCentimes: centimesPositifsSchema.default(0),
    verseLe: dateSaisieSchema.nullable().default(null),
});
export const versementSchema = champsVersementCrees.extend(champsSuiviPlateforme);
export const versementCreationSchema = champsVersementCrees;
/**
 * Un versement ne change ni de bénéficiaire ni de mois : la clé est unique.
 * `strict` : les transmettre est refusé, pas ignoré en silence.
 */
export const versementMiseAJourSchema = champsVersement
    .omit({ beneficiaireId: true, mois: true })
    .partial()
    .strict();
// ── Jalons — `platform_milestones` (4.7) ─────────────────────────────────────
export const ETATS_JALON = ['a_venir', 'en_cours', 'atteint', 'abandonne'];
export const etatJalonSchema = z.enum(ETATS_JALON);
const champsJalon = z.object({
    libelle: texte(160),
    echeanceLe: dateSaisieSchema.nullable(),
    etat: etatJalonSchema,
    ordre: z.int().min(0),
});
const champsJalonCrees = champsJalon.extend({
    echeanceLe: dateSaisieSchema.nullable().default(null),
    etat: etatJalonSchema.default('a_venir'),
    ordre: champsJalon.shape.ordre.default(0),
});
export const jalonSchema = champsJalonCrees.extend(champsSuiviPlateforme);
export const jalonCreationSchema = champsJalonCrees;
export const jalonMiseAJourSchema = champsJalon.partial();
//# sourceMappingURL=pilotage.js.map